/**
 * Identité affichée par l'écran de chargement : logo et nom de la clinique.
 *
 * Le splash d'`index.html` s'affiche AVANT que le code de l'application soit
 * chargé : il ne peut rien demander au serveur. Il lit donc ce que l'appli a
 * mémorisé à la visite précédente, dans le `localStorage` de CE domaine — chaque
 * abonné a son sous-domaine, donc son propre logo. Sans mémoire, c'est le logo
 * MADA. La clé et la forme sont partagées avec le script inline d'`index.html` :
 * les changer ici impose de les changer là-bas.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { api } from "@/lib/api/client";

export const SPLASH_STORAGE_KEY = "mada-splash";
export const DEFAULT_SPLASH_LOGO = "/favicon.svg";

export interface SplashBrand {
  logo: string | null;
  name: string | null;
}

/** Lecture défensive : une valeur abîmée ou absente donne l'identité MADA. */
export function readSplashBrand(): SplashBrand {
  try {
    const raw = localStorage.getItem(SPLASH_STORAGE_KEY);
    if (!raw) return { logo: null, name: null };
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return { logo: null, name: null };
    const { logo, name } = parsed as Record<string, unknown>;
    return {
      logo: typeof logo === "string" && logo ? logo : null,
      name: typeof name === "string" && name ? name : null,
    };
  } catch {
    return { logo: null, name: null };
  }
}

export function rememberSplashBrand(brand: SplashBrand): void {
  try {
    if (!brand.logo && !brand.name) {
      localStorage.removeItem(SPLASH_STORAGE_KEY);
      return;
    }
    localStorage.setItem(SPLASH_STORAGE_KEY, JSON.stringify(brand));
  } catch {
    /* stockage plein ou interdit : le splash retombera sur MADA */
  }
}

/** Identité publique du domaine courant (même source que l'écran de connexion). */
export function useBranding(clientSlug?: string, enabled = true) {
  const query = useQuery({
    queryKey: ["organisations", "branding", clientSlug ?? null],
    queryFn: async () => {
      const url = clientSlug
        ? `/organisations/branding/?client=${encodeURIComponent(clientSlug)}`
        : "/organisations/branding/";
      const { data } = await api.get<SplashBrand>(url);
      return data;
    },
    enabled,
    staleTime: 60 * 60 * 1000,
    retry: false,
  });
  const data = query.data;
  useEffect(() => {
    // Seule l'identité par défaut du domaine nourrit le splash : la page de
    // connexion d'un client précis (`?client=`) ne parle pas pour tout le domaine.
    if (data && !clientSlug) rememberSplashBrand({ logo: data.logo, name: data.name });
  }, [data, clientSlug]);
  return query;
}
